import clsx from "clsx";
import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../context/SettingsContext";

interface AccessibilityTooltipButtonProps {
  tooltipTitle: string;
  tooltipDescription: string;
  widgetButtonColor: string;
  tooltipAriaLabel?: string;
  shortcutKey?: string;
  tooltipAriaDescribedBy?: string;
  widgetButtonClassName?: string;
  tooltipLabel?: string;
}

const AccessibilityTooltipButton: React.FC<AccessibilityTooltipButtonProps> =
  React.memo(
    ({
      tooltipTitle,
      tooltipDescription,
      tooltipAriaLabel,
      shortcutKey,
      widgetButtonColor,
      tooltipAriaDescribedBy,
      widgetButtonClassName = "",
    }) => {
      const { t } = useTranslation();
      const { oversizeWidget } = useSettings();
      const [showTooltip, setShowTooltip] = React.useState(false);
      const [tooltipPosition, setTooltipPosition] = React.useState({
        x: 0,
        y: 0,
      });
      const buttonRef = React.useRef<HTMLButtonElement>(null);

      const handleMouseEnter = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setTooltipPosition({
            x: rect.left - 24,
            y: rect.top - 25,
          });
        }
        setShowTooltip(true);
      };

      const handleMouseLeave = () => {
        setShowTooltip(false);
      };

      const handleFocus = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setTooltipPosition({
            x: rect.left - 24,
            y: rect.top - 25,
          });
        }
        setShowTooltip(true);
      };

      const handleBlur = () => {
        setShowTooltip(false);
      };

      return (
        <>
          <div className="absolute left-4 top-4 group">
            <button
              ref={buttonRef}
              tabIndex={0}
              className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-300 text-white text-sm font-bold focus:outline-none hover:bg-gray-400"
              aria-label={t(tooltipAriaLabel)}
              aria-describedby={tooltipAriaDescribedBy}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              i
            </button>
          </div>

          {showTooltip &&
            createPortal(
              <div
                className="fixed text-black text-sm rounded-lg shadow-lg w-[220px] border-2"
                style={{
                  left: tooltipPosition.x,
                  top: tooltipPosition.y,
                  borderColor: widgetButtonColor,
                  transform: "translateY(-100%)",
                  zIndex: 999999,
                  backgroundColor: "white",
                  isolation: "isolate",
                }}
              >
                <div className="p-4 relative">
                  <h3 className="text-left font-semibold text-base mb-2">
                    {t(tooltipTitle)}
                  </h3>
                  <hr className="border-black border-t-2 pb-1" />

                  <p
                    className={clsx(
                      "text-xs mb-2 break-words text-left",
                      oversizeWidget ? "!font-semibold" : undefined
                    )}
                  >
                    {t(tooltipDescription)}
                  </p>
                  <p className="hidden sm:block text-xs text-right pr-2">
                    <span className="font-bold">{shortcutKey}</span>
                  </p>
                  <div
                    className="absolute top-full left-5 w-4 h-4 bg-white border-b-2 border-r-2"
                    style={{
                      borderColor: widgetButtonColor,
                      transform: "rotate(45deg) translateY(-50%)",
                    }}
                  ></div>
                </div>
              </div>,
              document.body
            )}
        </>
      );
    }
  );

AccessibilityTooltipButton.displayName = "AccessibilityTooltipButton";

export default AccessibilityTooltipButton;
